import React, { useContext, useRef, useMemo, useState } from 'react';
import { Context } from '@front10/helpers/dist/Context';
import { ThemeProvider } from '@front10/base-elements';
import { EmotionCache } from '../../containers/_commons/emotionCache';
/**
 * Atoms
 */
import { Listbox } from '../../lab/Listbox';
import Box from '../../elements/Box';
import Text from '../../elements/Text';
import { FilterButton } from '../../lab/FilterButton';
import { Panels } from '../../containers/VerticalBookingContainer/Panels';
import { FlexibleFilterContainer } from '../../lab/FlexibleFilterContainer';

/**
 * Hooks
 */
import { useIsMobileView } from '@front10/utils';

const StayLengthFilter = (props, ref) => {
  const {
    value,
    labelKey = 'cmp-filter-stay-length-title',
    placeholderLabelKey = 'cmp-filter-stay-length-placeholder',
    options = [],
    disabled,
    mobileView = false,
    onChange,
    filterButtonIconProps,
    menuContainer = {
      'cmp-styles': {
        width: '100%'
      }
    },
    items = {
      'cmp-styles': {
        height: '50px',
        '@theme': 'eds.typo.fontSize5'
      }
    },
    selectedItem = {
      fontWeight: 'bold',
      bgColor: 'colorBackgroundSoft',
      'cmp-styles': {
        '@theme': 'eds.typo.fontSize5',
        height: '50px',
        margin: '0px -20px',
        padding: '0px 20px'
      }
    },
    showDescriptionLabel = false
  } = props;

  const {
    translate,
    direction,
    theme: { designTokens }
  } = useContext(Context);

  const title = translate('cmp-filter-stay-length-title', 'Stay Length');
  const valueTranslated = useMemo(() => {
    return options
      .filter((option) => JSON.stringify(option.value) === JSON.stringify(value))
      .map((option) => {
        return translate(option.label);
      })
      .pop();
  }, [options, translate, value]);

  const stayLengthOptions = useMemo(() => {
    return (Array.isArray(options) ? options : []).map((option) => ({
      ...option,
      value: JSON.stringify(option.value),
      label: translate(option.label),
      testId: `stay-length-${Object.keys(option.value).length === 0 ? 'any' : option.value.maximum}`
    }));
  }, [options, translate]);

  const activeStayLength = useMemo(() => {
    return (
      Object.keys(value).length !== 0 &&
      stayLengthOptions.find((element) => element.value === JSON.stringify(value))
    );
  }, [value, stayLengthOptions]);

  const [stayLengthFilter, setStayLength] = useState(value);
  const flexibleRef = React.useRef();

  React.useEffect(() => {
    setStayLength(value);
  }, [value]);

  let isMobile = useIsMobileView();
  if (mobileView) {
    isMobile = true;
  }

  const listboxRef = useRef(null);

  React.useImperativeHandle(
    ref,
    () => ({
      focusToggleElement: () => {
        listboxRef.current.focusToggleElement();
      }
    }),
    []
  );

  const handleSave = (value1) => {
    props.onChange({ value: value1 });
    flexibleRef.current.save();
  };

  const desktopLayout = (
    <Listbox
      ref={listboxRef}
      label={translate(labelKey, 'Select Stay Length')}
      placeholder={translate(placeholderLabelKey, 'Any Nights')}
      options={stayLengthOptions}
      defaultValue={JSON.stringify(value)}
      activeFilterButton={disabled ? false : activeStayLength}
      forcePlaceholder={showDescriptionLabel && !disabled ? false : !activeStayLength || disabled}
      focusWhenIsShow={false}
      disabled={disabled}
      onChange={(value) => onChange({ value })}
      prefixSelectedItem={
        showDescriptionLabel
          ? translate('cmp-stay-length-filter-prefix-selected-item', 'Stay Length')
          : null
      }
      filterButtonIconProps={filterButtonIconProps}
      dataTestIdButton="stay-length-filter"
      dataTestIdPopover="stay-length-popover"
    />
  );

  const mobileLayout = (
    <FlexibleFilterContainer
      ref={flexibleRef}
      showSeparator
      saveOnClose={false}
      variant="layout.popover"
      label={title}
      title={title}
      displayFooter={false}
      toggleElement={
        <FilterButton
          iconSettings={filterButtonIconProps}
          value={
            value && !disabled && valueTranslated
              ? `${
                  showDescriptionLabel ? translate(labelKey, 'Stay Length') : ''
                } ${valueTranslated}`
              : translate(placeholderLabelKey, 'Any Nights')
          }
          aria-label={`${title} ${value ? valueTranslated : ''}`}
          active={disabled ? false : activeStayLength}
          disabled={disabled} // used to disabled the component
        />
      }
    >
      <Panels showSeparator={false}>
        <Box {...menuContainer}>
          {options.map((option) => (
            <Box
              display="flex"
              alignItems="center"
              {...items}
              {...(JSON.stringify(stayLengthFilter) === JSON.stringify(option.value) &&
                selectedItem)}
              onClick={() => handleSave(option.value)}
            >
              <Text>{translate(option.label)}</Text>
            </Box>
          ))}
        </Box>
      </Panels>
    </FlexibleFilterContainer>
  );

  return (
    <EmotionCache direction={direction}>
      <ThemeProvider theme={designTokens}>{!isMobile ? desktopLayout : mobileLayout}</ThemeProvider>
    </EmotionCache>
  );
};

export default React.forwardRef(StayLengthFilter);
